import { get, isInteger, isArray } from 'lodash';
import React, { memo } from 'react';
import styled from 'styled-components';

import Avatar from '../../Clients/Avatar';
import Name from '../../Clients/Name';
import TagList from '../../Tags/TagList';
import TicketStatus from '../TicketStatus';
import TicketType from '../TicketType';
import NewMessageIcon from '@icons/common/NewMessage';
import AvatarCrownIcon from '@icons/common/AvatarCrown';
import { TICKET_STATUS_PLACEMENT } from '../TicketStatus/constants';
import Timer from '../Timer';
import { StyledTimeFrom } from '../TicketStatus';
import { ITicketCard } from './types';

const TicketCard = ({
  className = '',
  client,
  ticket,
  planReason,
}: ITicketCard) => {
  if (!ticket) {
    return null;
  }

  const {
    read,
    status,
    subject,
    subtype,
    subAgentName,
    tags,
    type,
    hasPriorityService,
    isClosed,
    unreadMessengerAmount,
    sla,
    ltt,
    manager,
    assignedAt,
    createdAt,
    statusChangedAt,
  } = ticket;
  const { time, title, priority } = status;

  const hasUnreadMessage =
    !!unreadMessengerAmount &&
    isInteger(unreadMessengerAmount) &&
    unreadMessengerAmount > 0;

  const clientName = get(client, 'fullName') || 'Нет клиента';
  const avatarUrl = get(client, 'avatarUrl');

  // из assignedAt и statusChangedAt выбираем ту дату, которая позже (assignedAt - в формате ISO, statusChangedAt - в секундах)
  const assignedAtDate = new Date(assignedAt);
  const statusChangedAtDate = new Date(statusChangedAt * 1000);
  const slaSubtrahendTime = (
    assignedAtDate > statusChangedAtDate ? assignedAtDate : statusChangedAtDate
  ).toISOString();

  const lttSubtrahendTime = new Date(createdAt * 1000).toISOString();

  return (
    <StyledTicketCard className={className}>
      <StyledHeader>
        <StyledAvatarContainer>
          {hasPriorityService && !isClosed && (
            <StyledAvatarCrownIcon width={19} height={14} />
          )}
          <Avatar name={clientName} url={avatarUrl} />
        </StyledAvatarContainer>
        <StyledDescription>
          <StyledName name={clientName} />
          <StyledSubject title={subject}>{subject}</StyledSubject>
          <StyledTicketStatus
            planReason={planReason}
            priority={priority}
            time={time}
            title={title}
            placement={TICKET_STATUS_PLACEMENT.TICKET_CARD}
          />
          {ltt && (
            <StyledLTTTime
              subtrahendTime={lttSubtrahendTime}
              seconds={ltt}
              type="LTT"
            />
          )}
          {sla && manager && (
            <StyledSLATime
              subtrahendTime={slaSubtrahendTime}
              seconds={sla}
              type="SLA"
            />
          )}
        </StyledDescription>
      </StyledHeader>
      <StyledBody>
        <StyledFirstBodyRow>
          <StyledTicketType subtype={subtype} type={type} />
          <StyledCountContainer>
            {hasUnreadMessage && <NewMessageIcon width={17} />}
          </StyledCountContainer>
        </StyledFirstBodyRow>
        {!!subAgentName && (
          <StyledSubAgentName>{subAgentName}</StyledSubAgentName>
        )}
        <StyledTagList
          tags={tags}
          title={(isArray(tags) ? tags : []).join(', ')}
        />
      </StyledBody>
    </StyledTicketCard>
  );
};

TicketCard.defaultProps = {
  className: '',
  planReason: null,
};

const StyledTicketCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px 10px;
  width: 100%;
`;

const StyledHeader = styled.div`
  height: auto;
  display: flex;
  flex-shrink: 0;
  padding-right: 10px;
`;

const StyledAvatarContainer = styled.div`
  flex-shrink: 0;
  margin: 10px;
  margin-left: 0;
  position: relative;
`;

const StyledAvatarCrownIcon = styled(AvatarCrownIcon)`
  transform: rotate(-25deg);
  z-index: 1;
  position: absolute;
  top: -8px;
  left: 0px;
`;

const StyledDescription = styled.div`
  flex-grow: 1;
  overflow: hidden;
`;

const StyledName = styled(Name)`
  color: #919499;
  font-size: 12px;
  font-weight: normal;
  line-height: 18px;
  margin-top: 1px;
`;

const StyledSubject = styled.div`
  color: #17181a;
  font-size: 14px;
  font-weight: 700;
  line-height: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledTicketStatus = styled(TicketStatus)`
  margin-top: 2px;
  flex-wrap: wrap;

  ${StyledTimeFrom} {
    width: 100%;
    margin-left: 0;
  }
`;

const StyledBody = styled.div`
  display: flex;
  margin-top: 10px;
  flex-direction: column;
  flex-grow: 1;
`;

const StyledFirstBodyRow = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: space-between;
`;

const StyledTicketType = styled(TicketType)`
  margin-right: 12px;
`;

const StyledCountContainer = styled.div`
  width: 17px;
  display: flex;
  align-items: center;
`;

const StyledTagList = styled(TagList)`
  color: #919499;
  display: block;
  height: 17px;
  margin-top: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledSubAgentName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #4b137b;
  margin: 5px 0;
`;

const StyledSLATime = styled(Timer)`
  margin-bottom: 5px;
`;
const StyledLTTTime = styled(Timer)`
  margin: 0 5px 5px 0;
`;

export default memo(TicketCard);
